import { useState } from 'react'
import { Link } from 'react-router-dom'
import { usePaginatedQuery, useMutation } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Search, User, MoreVertical, RotateCcw, Loader2, Ban, CheckCircle } from 'lucide-react'
import { Id } from '../../../../convex/_generated/dataModel'

const PLAN_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  free: { bg: 'bg-neutral-100', text: 'text-neutral-600', label: 'Free' },
  pro: { bg: 'bg-green-100', text: 'text-green-700', label: 'Pro' },
  max: { bg: 'bg-violet-100', text: 'text-violet-700', label: 'Max' },
  ultra: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', label: 'Ultra' },
  'pro-trialing': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Pro (Trial)' },
  'pro-past_due': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pro (Past Due)' },
  'max-past_due': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Max (Past Due)' },
  'ultra-past_due': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Ultra (Past Due)' },
  'pro-incomplete': { bg: 'bg-red-100', text: 'text-red-700', label: 'Pro (Incomplete)' },
  'max-incomplete': { bg: 'bg-red-100', text: 'text-red-700', label: 'Max (Incomplete)' },
  'ultra-incomplete': { bg: 'bg-red-100', text: 'text-red-700', label: 'Ultra (Incomplete)' },
}

function PlanBadge({ plan }: { plan: string }) {
  const style = PLAN_STYLES[plan] ?? PLAN_STYLES.free
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  )
}


export default function AdminUsersPage() {
  const [search, setSearch] = useState('')
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  const { results: users, status, loadMore } = usePaginatedQuery(
    api.admin.listUsers,
    { search: search || undefined },
    { initialNumItems: 100 },
  )
  const isLoading = status === 'LoadingFirstPage'
  const canLoadMore = status === 'CanLoadMore'

  const resetCredits = useMutation(api.admin.resetUserCredits)
  const suspendUser = useMutation(api.admin.suspendUser)

  const handleResetCredits = async (userId: Id<"users">) => {
    if (confirm('Are you sure you want to reset this user\'s credits to 0?')) {
      await resetCredits({ userId })
      setOpenDropdown(null)
    }
  }

  const handleToggleSuspend = async (userId: Id<"users">, currentlySuspended: boolean) => {
    const action = currentlySuspended ? 'unsuspend' : 'suspend'
    if (confirm(`Are you sure you want to ${action} this user?`)) {
      await suspendUser({ userId, suspend: !currentlySuspended })
      setOpenDropdown(null)
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Users</h1>
        <p className="text-sm text-neutral-500">
          Platform users ({users.length} loaded)
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        <input
          type="text"
          placeholder="Search by email or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
        />
      </div>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Users</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-neutral-500 text-center py-8">
              No users found
            </p>
          ) : (
            <div>
              {/* Column Headers */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-200 mb-1">
                <span className="text-xs font-medium text-neutral-600 uppercase tracking-wide">
                  User
                </span>
                <div className="flex items-center gap-4">
                  <span className="text-xs font-medium text-neutral-600 uppercase tracking-wide w-24 text-right">
                    Plan
                  </span>
                  <span className="text-xs font-medium text-neutral-600 uppercase tracking-wide w-36 text-right">
                    Credits (Lifetime)
                  </span>
                  <span className="text-xs font-medium text-neutral-600 uppercase tracking-wide w-40 text-right">
                    Credits (This Month)
                  </span>
                  <span className="text-xs font-medium text-neutral-600 uppercase tracking-wide w-36 text-right">
                    Join Date
                  </span>
                  <div className="w-8" />
                </div>
              </div>

              {/* User Rows */}
              <div className="space-y-1">
                {users.map((user: any) => (
                  <Link
                    key={user._id}
                    to={`/admin/users/${user._id}`}
                    className="flex items-center justify-between p-3 hover:bg-neutral-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
                        <User className="h-5 w-5 text-neutral-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {user.name || 'No name'}
                        </p>
                        <p className="text-xs text-neutral-500">{user.email || 'No email'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-24 text-right">
                        <PlanBadge plan={user.effectivePlan} />
                      </div>
                      <p className="text-xs text-neutral-500 w-36 text-right">
                        {user.creditsUsedLifetime.toLocaleString()}
                      </p>
                      <p className="text-xs text-neutral-500 w-40 text-right">
                        {user.creditsUsedThisPeriod.toLocaleString()}
                      </p>
                      <p className="text-xs text-neutral-400 w-36 text-right">
                        {new Date(user._creationTime).toLocaleDateString()}{' '}
                        {new Date(user._creationTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </p>
                      {/* Actions Dropdown */}
                      <div className="relative" onClick={(e) => e.preventDefault()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.preventDefault()
                            setOpenDropdown(openDropdown === user._id ? null : user._id)
                          }}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                        {openDropdown === user._id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={(e) => {
                                e.preventDefault()
                                setOpenDropdown(null)
                              }}
                            />
                            <div className="absolute right-0 top-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 min-w-[180px] z-20">
                              <button
                                onClick={(e) => {
                                  e.preventDefault()
                                  handleResetCredits(user._id)
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2"
                              >
                                <RotateCcw className="h-4 w-4" />
                                Reset Credits to 0
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault()
                                  handleToggleSuspend(user._id, user.isSuspended)
                                }}
                                className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2 ${!user.isSuspended ? 'text-red-600' : 'text-green-600'}`}
                              >
                                {user.isSuspended ? (
                                  <><CheckCircle className="h-4 w-4" /> Unsuspend User</>
                                ) : (
                                  <><Ban className="h-4 w-4" /> Suspend User</>
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Load More */}
          {(canLoadMore || status === 'LoadingMore') && (
            <div className="flex justify-center pt-4 border-t border-neutral-100 mt-4">
              {status === 'LoadingMore' ? (
                <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadMore(100)}
                >
                  Load More
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
